import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Plan {
  id: number;
  name: string;
  description: string;
  price: string | number;
  features: string;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      console.log('Buscando planos...');
      const response = await api.get('/plans');
      console.log('Planos recebidos:', response.data);
      setPlans(response.data);
      if (response.data.length === 1) {
        setSelectedPlanId(response.data[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao carregar os planos disponíveis';
      setError(errorMessage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email é obrigatório');
      return false;
    }
    if (!formData.password) {
      setError('Senha é obrigatória');
      return false;
    }
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    if (!selectedPlanId) {
      setError('Por favor, selecione um plano');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await signup(formData.name, formData.email, formData.password, selectedPlanId!);
      navigate('/doctor/dashboard');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.response?.data?.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            align="center"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            TeleTranscrição
          </Typography>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center"
            sx={{ mb: 4 }}
          >
            Criar Conta
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <form onSubmit={handleSubmit} style={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Senha"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Confirmar Senha"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  required
                  variant="outlined"
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ 
                    mt: 2,
                    height: 48,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem'
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Criar Conta'
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{ 
                    mt: 2,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  Já tem uma conta? Faça login
                </Button>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Selecione seu plano
            </Typography>
            
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                variant="outlined"
                sx={{ 
                  mb: 2,
                  cursor: 'pointer',
                  borderColor: selectedPlanId === plan.id ? 'primary.main' : 'divider',
                  borderWidth: selectedPlanId === plan.id ? 2 : 1,
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {plan.description}
                  </Typography>
                  <Typography variant="h5" color="primary" gutterBottom>
                    R$ {typeof plan.price === 'number' ? plan.price.toFixed(2) : plan.price}/mês
                  </Typography>
                  <List dense>
                    {(() => {
                      let features = [];
                      try {
                        features = JSON.parse(plan.features || '[]');
                      } catch (e) {
                        features = plan.features ? [plan.features] : [];
                      }
                      return features.map((feature: string, index: number) => (
                        <ListItem key={index} disablePadding>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature.trim()} />
                        </ListItem>
                      ));
                    })()}
                  </List>
                </CardContent>
                <CardActions>
                  <Button 
                    fullWidth 
                    variant={selectedPlanId === plan.id ? "contained" : "outlined"}
                    onClick={() => setSelectedPlanId(plan.id)}
                  >
                    {selectedPlanId === plan.id ? 'Plano Selecionado' : 'Selecionar Plano'}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Signup;
